import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * LanguageToggle - iOS-style toggle component for EN/ID language switching
 *
 * Features:
 * - Square iOS-style toggle design
 * - Left position = English (EN)
 * - Right position = Indonesian (ID)
 * - Smooth animations between positions
 * - Integrates with LanguageContext for immediate language changes
 */
export const LanguageToggle: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const isEnglish = language === 'en';

  const handleToggle = () => {
    const newLanguage = isEnglish ? 'id' : 'en';
    setLanguage(newLanguage);
  };

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-muted-foreground whitespace-nowrap">
        {isEnglish ? 'Language' : 'Bahasa'}
      </span>
      <button
        onClick={handleToggle}
        className="relative bg-muted border-2 border-border rounded-lg p-1 w-24 h-9 transition-all duration-300 hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 flex-shrink-0"
        aria-label={`Switch to ${isEnglish ? 'Indonesian' : 'English'}`}
      >
        {/* Background slider */}
        <div
          className={`absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] bg-primary rounded-md shadow-md transition-all duration-300 ease-in-out ${
            isEnglish ? 'left-0.5' : 'left-[calc(50%+1px)]'
          }`}
        />

        {/* Language labels */}
        <div className="relative flex items-center justify-between h-full px-1.5">
          {/* English label */}
          <span
            className={`flex-1 text-center text-xs font-semibold transition-colors duration-300 z-10 ${
              isEnglish ? 'text-primary-foreground' : 'text-muted-foreground'
            }`}
          >
            EN
          </span>

          {/* Indonesian label */}
          <span
            className={`flex-1 text-center text-xs font-semibold transition-colors duration-300 z-10 ${
              !isEnglish ? 'text-primary-foreground' : 'text-muted-foreground'
            }`}
          >
            ID
          </span>
        </div>
      </button>
    </div>
  );
};
